import type { Translate } from "@khazzin/i18n";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Children, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { Icon } from "../../icons/Icon";
import { Button, IconButton } from "../../primitives/Button";
import { Badge } from "../../primitives/Display";
import { Drawer } from "../../primitives/Drawer";
import { Field } from "../../primitives/Field";
import { Input } from "../../primitives/Input";
import { matchOptions, Select } from "../../primitives/Select";
import { Checkbox, Switch } from "../../primitives/Selection";
import { useUiText } from "../../primitives/uiText";
import { cx } from "../../styles";
import { DatePicker } from "../form/DatePicker";
import { MoneyInput } from "../form/MoneyInput";
import { MultiSelect, type MultiSelectOption, SEARCH_FROM, toggled } from "./MultiSelect";

/**
 * A list's filters, and the URL that carries them.
 *
 * **The URL is the state.** Not React state that happens to be mirrored into
 * the address bar — the parameters are read on every render and written on
 * every change, so the screen has exactly one source of truth. That buys three
 * things a local-state version cannot have: a filtered list is a link somebody
 * can send to a colleague, the back button undoes a filter, and a reload lands
 * on the same rows rather than on an unfiltered list that looks like the filter
 * silently failed.
 *
 * **Search is debounced; the rest are not.** Typing is a stream and every
 * keystroke would be a request and a history entry; a select is one deliberate
 * choice. 300ms is long enough to swallow a word and short enough that a pause
 * feels answered.
 *
 * **The drawer applies on Apply.** A filter panel that re-queries on every
 * change makes a person watch four loading states while they set up one view,
 * and on a warehouse connection each of those is a second. Draft in the drawer,
 * one request on Apply.
 */

export type FilterKind =
	| "select"
	| "text"
	| "date"
	| "money"
	| "boolean"
	| "multiselect"
	| "tristate"
	| "number";

export interface FilterDefinition {
	/** The query parameter this filter writes. */
	id: string;
	label: string;
	kind: FilterKind;
	/** For `select`. */
	options?: { value: string; label: string }[];
	/** For `money`: which currency the amount is in. */
	currency?: string;
	hint?: string;
	/**
	 * Which section of the drawer this belongs under.
	 *
	 * Optional, and absent means the top. A drawer with nine sections — which
	 * is what the Orders list needs — is unreadable as one column of
	 * twenty-three fields, and the grouping is what lets somebody find the one
	 * they came for without reading the rest.
	 */
	group?: string;
	/**
	 * A heading inside a `multiselect`'s options, drawn above the option it
	 * sits on. `07/01` §3.1 groups the six exception statuses under their own
	 * header, and that grouping is the whole reason the picker is not a plain
	 * list of seventeen.
	 */
	optionHeadings?: Record<string, string>;
	/**
	 * Show a `multiselect` as a chip on the bar itself (ADJ-M03-1) rather than
	 * only in the drawer: for the one or two filters a screen is used by.
	 */
	inline?: boolean;
}

/** The active filters, as the URL holds them: everything is a string. */
export type FilterValues = Record<string, string>;

/**
 * Reads the filters out of the URL.
 *
 * Only the ids the caller declared. A stray parameter — a tracking tag, a
 * leftover from an older build — is left alone rather than fed into a query the
 * server would reject.
 */
export function readFilters(
	params: URLSearchParams,
	definitions: FilterDefinition[],
): FilterValues {
	const values: FilterValues = {};
	for (const definition of definitions) {
		const value = params.get(definition.id);
		if (value !== null && value !== "") {
			values[definition.id] = value;
		}
	}
	return values;
}

/** How long to wait after the last keystroke before changing the URL. */
export const SEARCH_DEBOUNCE_MS = 300;

/** The "no filter" choice in a select: Radix will not take "" as a value. */
const ANY = "__any__";

export interface SearchInputProps {
	value: string;
	/** Called with the text after `debounceMs` of quiet, or at once on Clear. */
	onValueChange: (value: string) => void;
	/** 0 commits every keystroke. A list that queries the server wants ~300. */
	debounceMs?: number;
	placeholder?: string | undefined;
	/** Defaults to "Search". */
	"aria-label"?: string | undefined;
	className?: string | undefined;
}

/**
 * The search field every list uses (ADJ-M03-1): a magnifier, a clear button,
 * and a debounce, so typing "corrugated mailer" is one request rather than
 * seventeen — the Orders page re-queried on every keystroke without one.
 *
 * It holds its own text so typing is never throttled by whoever owns `value`
 * (usually the URL), and adopts `value` when it changes underneath — the back
 * button, a pasted link, a Clear all.
 */
export function SearchInput({
	value,
	onValueChange,
	debounceMs = SEARCH_DEBOUNCE_MS,
	placeholder,
	"aria-label": ariaLabel,
	className,
}: SearchInputProps) {
	const t = useUiText();
	const [text, setText] = useState(value);
	// The latest callback, so a caller passing a fresh arrow each render does
	// not restart the timer on every render.
	const commit = useRef(onValueChange);
	commit.current = onValueChange;

	useEffect(() => {
		setText((current) => (current === value ? current : value));
	}, [value]);

	useEffect(() => {
		if (text === value) {
			return;
		}
		if (debounceMs <= 0) {
			commit.current(text);
			return;
		}
		const timer = window.setTimeout(() => commit.current(text), debounceMs);
		return () => window.clearTimeout(timer);
	}, [text, value, debounceMs]);

	return (
		<div className={cx("relative min-w-0", className)}>
			<Icon
				as={Search}
				size={15}
				className="pointer-events-none absolute inset-y-0 start-2.5 my-auto text-fg-subtle"
			/>
			<Input
				value={text}
				onChange={(event) => setText(event.target.value)}
				placeholder={placeholder}
				aria-label={ariaLabel ?? t("ui.actions.search")}
				className="ps-8 pe-9"
			/>
			{text !== "" && (
				<IconButton
					icon={X}
					label={t("ui.actions.clear")}
					size="sm"
					variant="ghost"
					onClick={() => {
						setText("");
						commit.current("");
					}}
					className="absolute inset-y-0 end-1 my-auto"
				/>
			)}
		</div>
	);
}

export interface FilterToolbarProps {
	/** The search field, usually a {@link SearchInput}. */
	search?: ReactNode;
	/** The filter chips, usually {@link MultiSelect}s, in reading order. */
	children?: ReactNode;
	/** How many filters are on, search included. Shows the count and Clear all. */
	activeCount?: number;
	onClearAll?: () => void;
	/** Opens the drawer of everything that is not a chip. */
	onMoreFilters?: () => void;
	/** How many of the drawer's filters are on, for its badge. */
	moreFiltersCount?: number;
	/** Trailing controls at the inline end: a column picker, a scan button. */
	end?: ReactNode;
	className?: string | undefined;
}

/**
 * A list's filter row (ADJ-M03-1): search, then the chips, then Filters for
 * the rest, then how many are on and Clear all, with anything else at the far
 * end. It wraps rather than scrolls, so nothing is ever out of reach on a
 * phone. Layout only: the state is the caller's, which is how the Orders page
 * keeps its own URL format and still gets the same row as every other list.
 */
export function FilterToolbar({
	search,
	children,
	activeCount = 0,
	onClearAll,
	onMoreFilters,
	moreFiltersCount = 0,
	end,
	className,
}: FilterToolbarProps) {
	const t = useUiText();
	return (
		<div className={cx("flex min-w-0 flex-1 flex-wrap items-center gap-2", className)}>
			{search !== undefined && <div className="min-w-0 flex-1 basis-56 sm:max-w-72">{search}</div>}
			{children}
			{onMoreFilters !== undefined && (
				<Button variant="secondary" icon={SlidersHorizontal} onClick={onMoreFilters}>
					{/* "More filters" once some are chips on the bar (ADJ-M03-3):
					    the drawer is then the rest, not all of them. */}
					{Children.count(children) > 0 ? t("ui.filters.more") : t("ui.filters.title")}
					{/* The count is the whole point of the badge: a list filtered by
					    something in a closed drawer looks like a list with missing
					    rows. */}
					{moreFiltersCount > 0 && <Badge tone="brand">{moreFiltersCount}</Badge>}
				</Button>
			)}
			{activeCount > 0 && onClearAll !== undefined && (
				<>
					<span className="text-[12px] text-fg-subtle [font-variant-numeric:tabular-nums] max-sm:hidden">
						{t("ui.filters.active", { count: activeCount })}
					</span>
					<Button variant="ghost" onClick={onClearAll}>
						{t("ui.filters.clearAll")}
					</Button>
				</>
			)}
			{end !== undefined && <div className="ms-auto flex flex-wrap items-center gap-2">{end}</div>}
		</div>
	);
}

export interface FilterBarProps {
	definitions: FilterDefinition[];
	t: Translate;
	searchPlaceholder?: string;
	/**
	 * Offer the search field. Default true; false for a list whose endpoint
	 * takes no `q`, where a box that filters nothing is worse than none.
	 */
	search?: boolean;
	/**
	 * Called after any filter or the search changes the URL — the moment a
	 * cursor-paged list must go back to page one, because the next page of the
	 * old filter is not a page of the new one.
	 */
	onChange?: () => void;
	/** The column picker, or anything else that belongs in the toolbar. */
	children?: ReactNode;
	className?: string;
}

/**
 * {@link FilterToolbar} wired to the URL: `q` for the search, one parameter
 * per definition. `inline` multiselects are chips on the bar; everything is
 * also in the drawer.
 */
export function FilterBar({
	definitions,
	t,
	searchPlaceholder,
	search = true,
	onChange,
	children,
	className,
}: FilterBarProps) {
	const [params, setParams] = useSearchParams();
	const changed = useRef(onChange);
	changed.current = onChange;
	const [open, setOpen] = useState(false);

	const active = useMemo(() => readFilters(params, definitions), [params, definitions]);
	const count = Object.keys(active).length;
	const queryParam = params.get("q") ?? "";

	const commitSearch = useCallback(
		(text: string) => {
			setParams(
				(current) => {
					const next = new URLSearchParams(current);
					if (text.trim() === "") {
						next.delete("q");
					} else {
						next.set("q", text);
					}
					return next;
				},
				// Replace rather than push: a history entry per search would mean
				// eleven back presses to leave a screen where somebody typed a SKU.
				{ replace: true },
			);
			changed.current?.();
		},
		[setParams],
	);

	/** Writes `values` for the declared filters; `clearSearch` drops `q` too. */
	const apply = useCallback(
		(values: FilterValues, clearSearch = false) => {
			setParams((current) => {
				const next = new URLSearchParams(current);
				for (const definition of definitions) {
					const value = values[definition.id];
					if (value === undefined || value === "") {
						next.delete(definition.id);
					} else {
						next.set(definition.id, value);
					}
				}
				if (clearSearch) {
					next.delete("q");
				}
				return next;
			});
			setOpen(false);
			changed.current?.();
		},
		[setParams, definitions],
	);

	const chips = definitions.filter(
		(definition) => definition.inline === true && definition.kind === "multiselect",
	);

	return (
		<>
			<FilterToolbar
				className={className}
				{...(search
					? {
							search: (
								<SearchInput
									value={queryParam}
									onValueChange={commitSearch}
									placeholder={searchPlaceholder}
									aria-label={t("ui.actions.search")}
								/>
							),
						}
					: {})}
				activeCount={count + (queryParam === "" ? 0 : 1)}
				onClearAll={() => apply({}, true)}
				{...(definitions.length > 0
					? { onMoreFilters: () => setOpen(true), moreFiltersCount: count }
					: {})}
				end={children}
			>
				{chips.map((definition) => (
					<MultiSelect
						key={definition.id}
						label={definition.label}
						options={optionsWithGroups(definition)}
						value={splitList(active[definition.id])}
						onValueChange={(next) => apply({ ...active, [definition.id]: next.join(",") })}
					/>
				))}
			</FilterToolbar>

			<FilterDrawer
				open={open}
				onOpenChange={setOpen}
				definitions={definitions}
				values={active}
				onApply={apply}
				t={t}
			/>
		</>
	);
}

/** A comma-separated URL value as a list; nothing for nothing. */
function splitList(value: string | undefined): string[] {
	return value === undefined || value === "" ? [] : value.split(",");
}

/**
 * A definition's options with `optionHeadings` carried down as groups: a
 * heading applies to its option and every one after it until the next.
 */
function optionsWithGroups(definition: FilterDefinition): MultiSelectOption[] {
	const headings = definition.optionHeadings ?? {};
	let group: string | undefined;
	return (definition.options ?? []).map((option) => {
		group = headings[option.value] ?? group;
		return group === undefined ? option : { ...option, group };
	});
}

export interface FilterDrawerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	definitions: FilterDefinition[];
	values: FilterValues;
	onApply: (values: FilterValues) => void;
	t: Translate;
	/** A bottom sheet on a phone rather than a page (ADJ-M03-3). */
	phone?: "page" | "sheet";
}

export function FilterDrawer({
	open,
	onOpenChange,
	definitions,
	values,
	onApply,
	t,
	phone = "page",
}: FilterDrawerProps) {
	// A draft, so nothing is queried until Apply. Re-seeded from the URL every
	// time the drawer opens, which is what makes Cancel mean cancel.
	const [draft, setDraft] = useState<FilterValues>(values);
	useEffect(() => {
		if (open) {
			setDraft(values);
		}
	}, [open, values]);

	function set(id: string, value: string) {
		setDraft((current) => ({ ...current, [id]: value }));
	}

	return (
		<Drawer
			open={open}
			onOpenChange={onOpenChange}
			title={t("ui.filters.title")}
			description={t("ui.filters.subtitle")}
			closeLabel={t("ui.actions.close")}
			backLabel={t("ui.actions.close")}
			phone={phone}
			footer={
				<>
					<Button variant="ghost" onClick={() => onApply({})}>
						{t("ui.filters.clearAll")}
					</Button>
					<Button variant="primary" onClick={() => onApply(draft)}>
						{t("ui.filters.apply")}
					</Button>
				</>
			}
		>
			{grouped(definitions).map(([group, members]) => (
				<section key={group} className="flex flex-col gap-3">
					{group !== "" && (
						<h3 className="border-border border-b pb-1 font-semibold text-[12px] text-fg-subtle uppercase tracking-wide">
							{group}
						</h3>
					)}
					{members.map((definition) => (
						<Field
							key={definition.id}
							label={definition.label}
							{...(definition.hint === undefined ? {} : { hint: definition.hint })}
						>
							{(props) => (
								<FilterControl
									definition={definition}
									value={draft[definition.id] ?? ""}
									onChange={(value) => set(definition.id, value)}
									controlProps={props}
									t={t}
								/>
							)}
						</Field>
					))}
				</section>
			))}
		</Drawer>
	);
}

/**
 * The definitions in section order, ungrouped ones first.
 *
 * Insertion order within a section, because the caller listed them in the
 * order somebody reads them — alphabetising a filter list puts "Attempts"
 * above "Status", which is nobody's priority.
 */
function grouped(definitions: FilterDefinition[]): [string, FilterDefinition[]][] {
	const sections = new Map<string, FilterDefinition[]>();
	for (const definition of definitions) {
		const key = definition.group ?? "";
		sections.set(key, [...(sections.get(key) ?? []), definition]);
	}
	return [...sections.entries()];
}

function FilterControl({
	definition,
	value,
	onChange,
	controlProps,
	t,
}: {
	definition: FilterDefinition;
	value: string;
	onChange: (value: string) => void;
	controlProps: {
		id: string;
		"aria-describedby": string | undefined;
		"aria-invalid": true | undefined;
	};
	t: Translate;
}) {
	switch (definition.kind) {
		case "select":
			return (
				<Select
					{...controlProps}
					// "Any" first (ADJ-M03-1): once a value was chosen there was no
					// way back to no filter short of Clear all.
					options={[{ value: ANY, label: t("ui.filters.any") }, ...(definition.options ?? [])]}
					value={value === "" ? ANY : value}
					onValueChange={(next) => onChange(next === ANY ? "" : next)}
					placeholder={t("ui.filters.any")}
				/>
			);
		case "date":
			return (
				<DatePicker
					{...controlProps}
					value={value === "" ? null : value}
					onChange={(next) => onChange(next ?? "")}
				/>
			);
		case "money":
			return (
				<MoneyInput
					{...controlProps}
					value={value === "" ? null : Number(value)}
					currency={definition.currency ?? "USD"}
					// Everything in the URL is a string; minor units go in as digits so
					// a shared link carries the same amount it was filtered by.
					onChange={(minor) => onChange(minor === null ? "" : String(minor))}
				/>
			);
		case "boolean":
			return (
				<Switch
					checked={value === "true"}
					onCheckedChange={(on) => onChange(on ? "true" : "")}
					aria-label={definition.label}
				/>
			);
		case "tristate":
			// Three states, because "either" is a real answer and is not the
			// same as "no". A switch cannot say it; a select can.
			return (
				<Select
					{...controlProps}
					options={[
						{ value: ANY, label: t("ui.filters.any") },
						{ value: "true", label: t("ui.filters.yes") },
						{ value: "false", label: t("ui.filters.no") },
					]}
					value={value === "" ? ANY : value}
					onValueChange={(next) => onChange(next === ANY ? "" : next)}
					placeholder={t("ui.filters.any")}
				/>
			);
		case "multiselect":
			return <MultiSelectFilter definition={definition} value={value} onChange={onChange} />;
		case "number":
			// A count or a number of hours (ADJ-M03-3): the numeric keypad on a
			// phone, and nothing a server would refuse as "not a whole number".
			return (
				<Input
					{...controlProps}
					type="number"
					min={0}
					step={1}
					inputMode="numeric"
					value={value}
					onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ""))}
				/>
			);
		default:
			return <Input {...controlProps} value={value} onChange={(e) => onChange(e.target.value)} />;
	}
}

/**
 * A checkbox list that writes one comma-separated value.
 *
 * Comma-separated because that is what the URL holds and what a saved view
 * stores — and because a link somebody retypes has to survive it. The server
 * takes the repeated spelling too, so nothing here constrains the API.
 *
 * Drawn as checkboxes rather than a multi-select popover: seventeen statuses
 * with six of them under their own heading is a thing people scan, and a
 * closed control that says "3 selected" hides exactly what they came to check.
 */
function MultiSelectFilter({
	definition,
	value,
	onChange,
}: {
	definition: FilterDefinition;
	value: string;
	onChange: (value: string) => void;
}) {
	const t = useUiText();
	const [query, setQuery] = useState("");
	const chosen = splitList(value);
	const options = optionsWithGroups(definition);
	// A search field on every non-empty list (owner, 2026-10-22).
	const shown = matchOptions(options, query);

	return (
		<div className="flex flex-col">
			{options.length > SEARCH_FROM && (
				<Input
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					placeholder={t("ui.actions.search")}
					aria-label={`${t("ui.actions.search")}: ${definition.label}`}
					className="mb-1"
				/>
			)}
			{shown.map((option, index) => (
				<div key={option.value} className="flex flex-col">
					{option.group !== undefined && option.group !== shown[index - 1]?.group && (
						<p className="mt-2 mb-1 font-semibold text-[11px] text-fg-subtle uppercase">
							{option.group}
						</p>
					)}
					{/* The primitive (ADJ-M03-1): the whole row is the target, 48px
					    on a phone, rather than a bare 13px native box. */}
					<Checkbox
						checked={chosen.includes(option.value)}
						onCheckedChange={() => onChange(toggled(chosen, option.value).join(","))}
						label={option.label}
						className="min-h-8 w-full rounded-sm px-1 hover:bg-bg-subtle"
					/>
				</div>
			))}
		</div>
	);
}
